import { useEffect, useState } from 'react';

// Front grid size in px (must match index.css front grid)
const GRID = 40;

interface Pulse {
  id: string;
  direction: 'horizontal' | 'vertical';
  reverse: boolean; // travel negative direction
  // absolute pixel position snapped to the grid
  startPosition: { x: number; y: number };
  // travel distance in px
  length: number;
  duration: number;
  brightness: number; // 1.0 – 1.3
}

// Use the actual grid line color (sg-border: #1e1e2e => rgb(30,30,46))
const GRID_LINE = 'rgba(30, 30, 46, 0.32)';
const GRID_LINE_CORE = 'rgba(30, 30, 46, 0.85)';

export function GridPulses() {
  const [pulses, setPulses] = useState<Pulse[]>([]);
  const [vw, setVw] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const [vh, setVh] = useState<number>(typeof window !== 'undefined' ? window.innerHeight : 1080);

  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const snap = (value: number, step: number) => Math.max(0, Math.round(value / step) * step);

    const createPulse = () => {
      const direction = Math.random() > 0.5 ? 'horizontal' : 'vertical';
      const reverse = Math.random() < 0.5;

// 50% chance to run the full remaining length of the grid line
      const longRun = Math.random() < 0.5;

      let x = 0, y = 0, length = 0;
      if (direction === 'horizontal') {
        y = snap(Math.random() * vh, GRID);
        if (reverse) {
          // moving left ←
          x = Math.max(0, Math.random() * vw);
          // up to left edge
length = longRun ? Math.max(GRID, x - 2) : (GRID + 10) + Math.random() * (GRID - 10); // 50–80px
          // ensure start keeps element in bounds
          x = Math.min(vw - 2, Math.max(GRID, x));
        } else {
          // moving right →
          x = Math.max(0, Math.random() * vw);
length = longRun ? Math.max(GRID, vw - x - 2) : (GRID + 10) + Math.random() * (GRID - 10); // 50–80px
          x = Math.min(vw - length - 2, Math.max(0, x));
        }
      } else {
        x = snap(Math.random() * vw, GRID);
        if (reverse) {
          // moving up ↑
          y = Math.max(0, Math.random() * vh);
length = longRun ? Math.max(GRID, y - 2) : (GRID + 10) + Math.random() * (GRID - 10);
          y = Math.min(vh - 2, Math.max(GRID, y));
        } else {
          // moving down ↓
          y = Math.max(0, Math.random() * vh);
length = longRun ? Math.max(GRID, vh - y - 2) : (GRID + 10) + Math.random() * (GRID - 10);
          y = Math.min(vh - length - 2, Math.max(0, y));
        }
      }

// Velocity-based duration with narrow jitter to keep feel consistent
      const pxPerSec = 420 + Math.random() * 120; // 420–540 px/s (~±12%)
      let duration = length / pxPerSec; // seconds
      duration = Math.max(0.18, Math.min(0.55, duration));

      const pulse: Pulse = {
        id: `${Date.now()}-${Math.random()}`,
        direction,
        reverse,
        startPosition: { x, y },
        length,
        duration,
        brightness: 1 + Math.random() * 0.3,
      };

      setPulses((prev) => {
const next = [...prev, pulse];
        if (next.length > 90) next.splice(0, next.length - 90); // cap raised for concurrency
        return next;
      });

      setTimeout(() => {
        setPulses((prev) => prev.filter((p) => p.id !== pulse.id));
      }, duration * 1000);
    };

    const createBurst = (count: number) => {
      for (let i = 0; i < count; i++) {
        setTimeout(createPulse, i * (50 + Math.random() * 50));
      }
    };

    // Synchronized micro-wave: coordinated pulse on adjacent lines
    const createMicroWave = () => {
      const waveDirection = Math.random() > 0.5 ? 'horizontal' : 'vertical';
      const waveReverse = Math.random() < 0.5;
      const waveCount = 5 + Math.floor(Math.random() * 4); // 5–8 beams
      
      // Pick starting edge based on direction + reverse
      let edgeOffset = 0;
      if (waveDirection === 'horizontal') {
        edgeOffset = waveReverse ? vw - GRID : 0; // left edge or right edge
      } else {
        edgeOffset = waveReverse ? vh - GRID : 0; // top edge or bottom edge
      }

      for (let i = 0; i < waveCount; i++) {
        setTimeout(() => {
          const direction = waveDirection;
          const reverse = waveReverse;
          let x = 0, y = 0, length = 0;

          if (direction === 'horizontal') {
            y = snap(Math.random() * vh, GRID); // random row
            if (reverse) {
              // all moving left from right edge
              x = edgeOffset;
              length = GRID * 2 + Math.random() * (GRID * 2); // 80–160px wave
            } else {
              // all moving right from left edge
              x = edgeOffset;
              length = GRID * 2 + Math.random() * (GRID * 2);
            }
          } else {
            x = snap(Math.random() * vw, GRID); // random column
            if (reverse) {
              // all moving up from bottom edge
              y = edgeOffset;
              length = GRID * 2 + Math.random() * (GRID * 2);
            } else {
              // all moving down from top edge
              y = edgeOffset;
              length = GRID * 2 + Math.random() * (GRID * 2);
            }
          }

          const pxPerSec = 420 + Math.random() * 120;
          let duration = length / pxPerSec;
          duration = Math.max(0.18, Math.min(0.55, duration));

          const pulse: Pulse = {
            id: `wave-${Date.now()}-${Math.random()}`,
            direction,
            reverse,
            startPosition: { x, y },
            length,
            duration,
            brightness: 1 + Math.random() * 0.3,
          };

          setPulses((prev) => {
            const next = [...prev, pulse];
            if (next.length > 90) next.splice(0, next.length - 90);
            return next;
          });

          setTimeout(() => {
            setPulses((prev) => prev.filter((p) => p.id !== pulse.id));
          }, duration * 1000);
        }, i * (50 + Math.random() * 100)); // 50–150ms stagger between beams
      }
    };

    // Seed with a small burst
    createBurst(5);

    // Frequent small bursts for concurrency (multiple active at once)
    let cancelled = false;
const scheduleNextPulse = () => {
      const delay = 150 + Math.random() * 300; // 150–450ms (more frequent)
      setTimeout(() => {
        if (cancelled) return;
        createBurst(3 + Math.floor(Math.random() * 4)); // 3–6 pulses per burst
        scheduleNextPulse();
      }, delay);
    };

    scheduleNextPulse();

    // Schedule micro-waves every 8–12 seconds
    const scheduleMicroWave = () => {
      const delay = 8000 + Math.random() * 4000; // 8–12s
      setTimeout(() => {
        if (cancelled) return;
        createMicroWave();
        scheduleMicroWave();
      }, delay);
    };
    scheduleMicroWave();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none z-[1]">
      {pulses.map((pulse) => (
        <div key={pulse.id}>
          {/* Comet tail (fades toward tail) */}
          <div
            className="absolute"
            style={{
              top: `${pulse.startPosition.y}px`,
              left: `${pulse.startPosition.x}px`,
              width: pulse.direction === 'horizontal' ? `${pulse.length}px` : '1px',
              height: pulse.direction === 'horizontal' ? '1px' : `${pulse.length}px`,
background:
                pulse.direction === 'horizontal'
                  ? (pulse.reverse
                      ? `linear-gradient(90deg, ${GRID_LINE_CORE} 0%, ${GRID_LINE} 40%, transparent 100%)` // moving left: brighter/longer head
                      : `linear-gradient(90deg, transparent 0%, ${GRID_LINE} 60%, ${GRID_LINE_CORE} 100%)`) // moving right
                  : (pulse.reverse
                      ? `linear-gradient(180deg, ${GRID_LINE_CORE} 0%, ${GRID_LINE} 40%, transparent 100%)` // moving up
                      : `linear-gradient(180deg, transparent 0%, ${GRID_LINE} 60%, ${GRID_LINE_CORE} 100%)`), // moving down
              animation: `${pulse.direction === 'horizontal'
                ? (pulse.reverse ? 'travelHorizontalRev' : 'travelHorizontal')
                : (pulse.reverse ? 'travelVerticalRev' : 'travelVertical')}
                ${pulse.duration}s linear forwards`,
              willChange: 'transform, opacity, filter',
              opacity: 0.82,
              filter: `brightness(${pulse.brightness})`,
            }}
          />

        </div>
      ))}
      <style>{`
        @keyframes travelHorizontal {
          0% { transform: translateX(0); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes travelHorizontalRev {
          0% { transform: translateX(0); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateX(-100%); opacity: 0; }
        }
        @keyframes travelVertical {
          0% { transform: translateY(0); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        @keyframes travelVerticalRev {
          0% { transform: translateY(0); opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { transform: translateY(-100%); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
